import { supportedLanguages } from '../config/settings/app.settings';
import { getAccessToken } from './tokens';
import { errors } from '../tools/errors';
import * as log from './log';
import { request } from './http';
import { config } from '@worktango/ai-assistant/react';

type SimplifiedHeaders = Record<string, string>;
type ExtractedHeaders = { contentType: string | undefined; headers: SimplifiedHeaders };

export function extractContentType(headers: SimplifiedHeaders): ExtractedHeaders {
  // Using both strings to support previous implementations.
  const { ['content-type']: contentType1, ['Content-Type']: contentType2, ...rest } = headers ?? {};
  return {
    contentType: contentType1 || contentType2 || undefined,
    headers: rest,
  };
}

type GetHeadersOptions = {
  // I tried using HeadersInit here but it was not working as expected.
  extraHeaders?: SimplifiedHeaders;
  skipAuth?: boolean;
  skipContentType?: boolean;
  hasBody?: boolean;
  contentType?: string | undefined;
};

export function getHeaders({
  extraHeaders = {},
  skipAuth = false,
  skipContentType = false,
  hasBody = false,
  contentType = undefined,
}: GetHeadersOptions = {}): Record<string, string> {
  let usedContentType = contentType;

  // If no custom content-type was defined and there is data in the body, and
  // the request isn't skipping content-type, then default to JSON.
  if (!contentType && !skipContentType && hasBody) {
    usedContentType = 'application/json';
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': supportedLanguages.join(', '),
    ...(extraHeaders ?? {}),
    ...(usedContentType ? { 'Content-Type': usedContentType } : {}),
  };

  if (!skipAuth) {
    const authToken = getAccessToken();

    // If there is no auth token found, it is either not yet set, or it is expired.
    // A missing token is effectively "expired".
    if (!authToken) {
      const tokenExpiredError = errors.createError({
        name: errors.types.tokenExpired,
        message: 'please login again',
      });

      log.error('http token expired: ', tokenExpiredError);

      throw tokenExpiredError; // will trigger calling redux action to be rejected
    }

    // Override any existing Authorization with authToken.
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  return headers;
}

type GetBodyOptions = {
  body?: unknown;
  skipContentType?: boolean;
  contentType?: string | undefined;
};

export function getBody({ body, skipContentType, contentType }: GetBodyOptions): unknown {
  let usedBody = body;

  if (!contentType && !skipContentType && body) {
    usedBody = JSON.stringify(body);
  }
  return usedBody;
}

type HttpRequestOptions = {
  skipContentType?: boolean;
  skipAuth?: boolean;
  headers?: SimplifiedHeaders;
  body?: unknown;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS';
};

/**
 * Make regular HTTP network request using http adapter.
 *
 * These default values are specific to the WorkTango API, which is a purely
 * JSON-based API. The 'accept-language' list is also always the same based on
 * this client app's supported languages.
 *
 * If `skipContentType` was set, remove it from `options` so it doesn't get
 * added to the `http.request()` call. This is a special custom parameter to
 * avoid defaulting to 'application/json' if you don't want that. Most of the
 * time our API is a JSON api and we do want it to default to 'application/json'.
 */
export async function httpRequest<T>(
  url: string,
  opts: HttpRequestOptions = {
    skipContentType: false,
    skipAuth: false,
  },
): Promise<T> {
  const pulseUrl = config.getPulseUrl(url);

  // Don't include custom (non-fetch) options.
  const { skipContentType, skipAuth, ...options } = opts;

  let { contentType, headers: extraHeaders } = extractContentType(options.headers ?? {});

  const headers = getHeaders({
    extraHeaders: extraHeaders,
    contentType: contentType,
    skipAuth,
    skipContentType,
    hasBody: !!options.body,
  });

  const body = getBody({ body: options.body, skipContentType, contentType });

  const res = await request(pulseUrl, {
    method: 'GET',
    ...options, // will override `method` if it is defined by the user
    headers,
    body,
  });

  // If intentionally expecting a non-json response, just return the raw response.
  if (headers.Accept !== 'application/json') {
    return res;
  }

  // Otherwise, by default, parse the response as json and return the resulting object.
  return res.json();
}
